import Head from 'next/head';
import { NextRouter, useRouter } from 'next/router';

import { t } from '@/utils/helper/intl';
import {
  aiRemovalURL,
  aiVideoEditURL,
  avatarVideoProURL,
  faceSwapperURL,
  homeURL,
  imageEnhanceURL,
  imageGenerateURL,
  imageTranslationURL,
  IndexURL,
  libraryURL,
  notFoundURL,
  PolicyURL,
  pricingURL,
  productAvatarURL,
  TermsURL,
  videoGenerateURL,
  videoTranslateURL,
  voiceCloneURL,
} from '@/utils/routeConstants';

const sameAs = [
  'https://www.instagram.com/falcocutai_official',
  'https://www.tiktok.com/@falcocutai',
  'https://x.com/falcocut_ai',
];

const softwareOrg = {
  '@type': 'SoftwareApplication',
  name: 'Falcocut',
  description:
    'FalcoCut is an AI content creation platform for marketing teams to translate videos, generate avatars, clone voices, swap faces, and produce videos or images from simple prompts—faster than traditional workflows.',
  applicationCategory: 'BusinessApplication',
  url: 'https://falcocut.ai/',
  image: 'https://falcocut.ai/images/logo-with-name.png',
  inLanguage: ['en'],
  softwareVersion: '-',
  author: '-',

  aggregateRating: {
    ratingValue: 4.8,
    ratingCount: 937,
    reviewCount: 937,
  },
  review: '-',
  offers: {
    price: '-',
    softwareHelp: '-',
    featureList: '-',
    priceCurrency: 'USD',
    availability: 'Instock',
    screenshot: '-',
    sameAs,
  },
  applicationSubCategory: '-',
  downloadUrl: '-',
  operatingSystem: 'Web',
};
const org = {
  name: 'Falcocut',
  url: 'https://falcocut.ai/',
  logo: 'https://falcocut.ai/images/logo-with-name.png',
  contactPoint: {
    email: 'support@falcocut.ai',
  },
  sameAs,
};

const findConfig = (list: any[], path: string): any => {
  for (const item of list) {
    if (item?.key === path) {
      return item;
    }
    if (item?.children && item.children.length > 0) {
      const childConfig = findConfig(item.children, path);
      if (childConfig) return childConfig;
    }
  }
  return null;
};
const getMeta = (router: NextRouter) => {
  const siteUrl = 'https://falcocut.ai';
  // 简单的 URL 拼接，实际生产中可能需要更严谨的处理
  const currentUrl = `${siteUrl}${router.asPath}`;
  // 默认图片，建议替换为实际的 OG 图片地址
  const defaultImage = `https://falcocut.ai/images/og-img.png`;

  const allPageList: any = [
    {
      key: IndexURL,
      metaTitle: t('FalcoCut-Best-AI-Video-Generator-for-Avatars-Voice'),
      metaDescription: t('http-falcocut-ai-FalcoCut-Best-AI-Video-Generator'),
    },
    {
      key: homeURL,
      metaTitle: t('Home-Falcocut'),
      metaDescription: t('Start-creating-with-FalcoCuts-AI-tools-for-video-t'),
    },
    {
      key: videoGenerateURL,
      metaTitle: t('fc.86663f34'),
      metaDescription: t('fc.a0819ff4'),
    },
    {
      key: imageGenerateURL,
      metaTitle: t('fc.3fbdf644'),
      metaDescription: t('fc.6faa2cf0'),
    },
    {
      key: videoTranslateURL,
      metaTitle: t('AI-Video-Translator-with-Subtitles-Dubbing-and-Lip'),
      metaDescription: t('FalcoCut-AI-Video-Translator-lets-you-translate-vi'),
    },
    {
      key: avatarVideoProURL,
      metaTitle: t('Best-AI-Avatar-Generator-Create-Realistic-Talking'),
      metaDescription: t('Generate-lifelike-AI-avatars-in-minutes-with-Falco'),
    },
    {
      key: productAvatarURL,
      metaTitle: t('AI-Product-Avatar-Let-Digital-Avatars-Hold-and-Dis'),
      metaDescription: t('Create-product-visuals-instantly-with-AI-avatars-U'),
    },
    {
      key: faceSwapperURL,
      metaTitle: t('Free-AI-Face-Swap-Online-for-Photos-and-Videos'),
      metaDescription: t('Instantly-swap-faces-in-photos-and-videos-with-Fal'),
    },
    {
      key: voiceCloneURL,
      metaTitle: t('AI-Voice-Cloning-Online-Clone-Any-Voice-and-Multil'),
      metaDescription: t('Clone-any-voice-online-free-with-FalcoCuts-AI-voic'),
    },
    {
      key: libraryURL,
      metaTitle: t('FalcoCut-2'),
      metaDescription: t('FalcoCut--AI--'),
    },

    {
      key: pricingURL,
      metaTitle: t('c6db8605a2b080640378ef6ad0d2fe0c'),
      metaDescription: t('Explore-FalcoCuts-AI-video-and-image-plans-for-mar'),
    },
    {
      key: notFoundURL,
      metaTitle: t('Page-Not-Found'),
      metaDescription: t('The-page-youre-looking-for-doesnt-exist-or-has-bee'),
    },
    {
      key: PolicyURL,
      metaTitle: t('Privacy-Policy-FalcoCut'),
      metaDescription: t('Read-FalcoCuts-Privacy-Policy-to-understand-how-we'),
    },
    {
      key: PolicyURL,
      metaTitle: t('Privacy-Policy-FalcoCut'),
      metaDescription: t('Read-FalcoCuts-Privacy-Policy-to-understand-how-we'),
    },
    {
      key: TermsURL,
      metaTitle: t('Terms-Falcocut'),
      metaDescription: t('Review-the-Terms-and-Conditions-for-FalcoCut-cover'),
    },
    {
      key: imageEnhanceURL,
      metaTitle: t('58e25d7c5405967351f1838b3e80b0a8'),
      metaDescription: t('AI-10'),
    },
    {
      key: aiRemovalURL,
      metaTitle: t('a6f56ee540fd17ec6274f088bdb3b46b'),
      metaDescription: t('AI-AI'),
    },
    {
      key: imageTranslationURL,
      metaTitle: t('e0772dbaa0c17f4143eada1fa54e3fbf'),
      metaDescription: t('OCR-100'),
    },
    {
      key: aiVideoEditURL,
      metaTitle: t('AI-AI-FalcoCut'),
      metaDescription: t('FalcoCut-AI'),
    },
  ];

  const config = findConfig(allPageList, router.pathname);
  let title = '';
  let description = '';

  if (config) {
    title = config?.metaTitle;
    description = config?.metaDescription;
  }

  return { title, description, currentUrl, defaultImage };
};

const PageHead = () => {
  const router = useRouter();

  const { title, description, currentUrl, defaultImage } = getMeta(router);

  // 使用了 SEOHead 的页面，不需要 PageHead
  const pagesWithSEOHead = [
    videoTranslateURL,
    avatarVideoProURL,
    voiceCloneURL,
    videoGenerateURL,
    '/ai-tiktok-video-generator',
  ];

  // 检查是否是使用了 SEOHead 的页面（包括动态路由）
  const hasSEOHead =
    pagesWithSEOHead.includes(router.pathname) ||
    router.pathname.startsWith('/tools/video-translator/');

  // 如果页面使用了 SEOHead，则不渲染 PageHead
  if (hasSEOHead) {
    return null;
  }

  const orgArr = ['/', homeURL];
  const softwareArr = [productAvatarURL, faceSwapperURL, imageGenerateURL];
  let schema = {};
  if (orgArr.includes(router.asPath)) {
    schema = org;
  } else if (softwareArr.includes(router.asPath)) {
    schema = softwareOrg;
  }

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={currentUrl}></link>

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="falcocut.ai" />
      <meta property="og:image" content={defaultImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@falcocut" />
      <meta name="twitter:site:id" content="falcocut" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={defaultImage} />
      <meta name="twitter:image:alt" content="Falcocut" />
      {/* <script type="application/ld+json">{JSON.stringify()}</script> */}
      <script
        type="application/ld+json"
        // 使用 JSON.stringify 保证合法 JSON 输出
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        key="ldjson-product"
      />
    </Head>
  );
};

export default PageHead;
